import { forwardRef, ComponentProps, type JSX } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@restworld/utils-common';

import { Spinner } from '../spinner';
import { focusRing, focusRingReversed } from '../styles/focus-ring';

const iconButtonVariants = cva(
  // Shared WCAG focus ring (purple-500); the reversed variant overrides it to white.
  `border-none outline-none p-0 m-0 text-inherit hover:cursor-pointer grid place-items-center transition duration-200 ease-in-out ${focusRing}`,
  {
    variants: {
      size: {
        sm: '!h-6 !w-6 rounded-[4px] active:h-[22px] active:w-[22px]',
        md: '!h-10 !w-10 rounded-lg active:h-[38px] active:w-[38px]',
        lg: 'h-12 w-12 rounded-xl',
      },
      variant: {
        neutral: 'text-neutral-700 hover:bg-neutral-900/10 active:bg-neutral-900/20',
        success: 'text-green-700 hover:bg-green-700/10 active:bg-green-700/20',
        warning: 'text-orange-700 hover:bg-orange-700/10 active:bg-orange-700/20',
        error: 'text-red-700 hover:bg-red-700/10 active:bg-red-700/20',
        restworld: 'text-purple-700 hover:bg-purple-700/10 active:bg-purple-700/20',
        reversed: `text-white bg-white/10 active:bg-white ${focusRingReversed}`,
      },
    },
    defaultVariants: {
      size: 'md',
      variant: 'neutral',
    },
  }
);

const iconButtonWrapperVariants = cva('grid place-items-center', {
  variants: {
    size: {
      sm: 'h-[26px] w-[26px]',
      md: 'h-[42px] w-[42px]',
      lg: 'h-[50px] w-12',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const iconButtonLoadingVariants = cva('', {
  variants: {
    variant: {
      neutral: 'bg-neutral-700/10 hover:bg-neutral-900/10',
      success: 'bg-green-700/10 hover:bg-green-700/10',
      warning: 'bg-orange-700/10 hover:bg-orange-700/10',
      error: 'bg-red-700/10 hover:bg-red-700/10',
      restworld: 'bg-purple-700/10 hover:bg-purple-700/10',
      reversed: 'bg-white hover:bg-white',
    },
  },
});

/** Solid per-variant background, opted into via the `withBg` prop. */
const iconButtonBgVariants = cva('', {
  variants: {
    variant: {
      neutral: 'bg-neutral-50 hover:bg-neutral-100',
      success: 'bg-green-50 hover:bg-green-100',
      warning: 'bg-orange-50 hover:bg-orange-100',
      error: 'bg-red-50 hover:bg-red-100',
      restworld: 'bg-purple-50 hover:bg-purple-100',
      reversed: 'bg-white hover:bg-white',
    },
  },
});

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>((props, ref) => {
  const {
    Icon,
    ariaLabel,
    onClick,
    size = 'md',
    variant = 'neutral',
    loading,
    withBg,
    wrapperStyle,
    className,
    ...restProps
  } = props;

  const iconSizeClasses = size === 'sm' ? '!h-5 !w-5' : size === 'lg' ? '!h-7 !w-7' : '!h-6 !w-6';

  return (
    <span className={cn(iconButtonWrapperVariants({ size }), wrapperStyle)}>
      <button
        aria-label={ariaLabel}
        onClick={onClick}
        ref={ref}
        className={cn(
          iconButtonVariants({ size, variant }),
          withBg && iconButtonBgVariants({ variant }),
          (restProps.disabled || loading) && 'text-neutral-100 hover:bg-transparent hover:cursor-not-allowed',
          loading && iconButtonLoadingVariants({ variant }),
          className
        )}
        disabled={restProps.disabled}
        {...restProps}
      >
        {loading ? (
          <Spinner className={cn('flex justify-center items-center', iconSizeClasses)} color={variant} />
        ) : (
          <Icon className={iconSizeClasses} />
        )}
      </button>
    </span>
  );
});
export default IconButton;

export type IconComponentType = (props: ComponentProps<'svg'>) => React.ReactNode;
export interface IconButtonProps extends Omit<ComponentProps<'button'>, 'aria-label'> {
  Icon: IconComponentType;
  ariaLabel: string;
  disabled?: boolean;
  /** Additional class names applied to the button element */
  className?: string;
  loading?: boolean;
  size?: IconButtonSizeType;
  variant?: IconButtonVariantType;
  /** Apply a solid per-variant background to the button. */
  withBg?: boolean;
  /** Additional class names applied to the wrapper span element */
  wrapperStyle?: string;
}
type IconButtonSizeType = 'sm' | 'md' | 'lg';
export type IconButtonVariantType = 'neutral' | 'success' | 'warning' | 'error' | 'restworld' | 'reversed';

export { iconButtonVariants, iconButtonWrapperVariants, iconButtonLoadingVariants };
